import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { useTMInvoiceData, computeTMTimeTotals } from "@/hooks/use-tm-invoice-data";
import type { TMInvoiceContext } from "./CreateInvoiceForm";

interface VoucherState {
  include: boolean;
  markupPct: number;
}

/** Forhåndsutfylling som sendes videre til den vanlige faktura-dialogen. */
export interface TMInvoicePrefill {
  description: string;
  type: "final";
  lines: { description: string; quantity: number; unit_price: number }[];
  tmContext: TMInvoiceContext;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * VIB-1186: T&M-steget. Lar PL sette timesats per ansatt (VIB-1183) og velge
 * bilag, og bygger så fakturalinjene + T&M-kontekst som sendes videre til den
 * vanlige faktura-dialogen (CreateInvoiceForm) via onProceed. Oppretter ikke
 * fakturaen selv — det skjer i fellesdialogen med send-/leverings-/forfall-valg.
 */
export function TMInvoicePreviewDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
  onProceed,
  upsellId,
}: {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: (prefill: TMInvoicePrefill) => void;
  /** VIB-994: satt = scopet til ett T&M-mersalg. */
  upsellId?: string;
}) {
  const { data, isLoading } = useTMInvoiceData(projectId, upsellId ? { upsellId } : undefined);

  const [voucherState, setVoucherState] = useState<Record<string, VoucherState>>({});
  // VIB-1183: timesats redigeres direkte i fakturaen, per ansatt-linje.
  // Forhåndsutfylles fra ansattens hourly_sales_rate hvis satt, ellers tomt.
  // "" = ikke fylt inn (blokkerer videre). Lagres ikke utenfor fakturaen.
  const [rateState, setRateState] = useState<Record<string, number | "">>({});

  // Init voucher- og sats-state når data lastes.
  useEffect(() => {
    if (!data) return;
    setVoucherState((prev) => {
      const next = { ...prev };
      for (const v of data.vouchers) {
        if (!next[v.id]) next[v.id] = { include: true, markupPct: data.defaultMarkupPct };
      }
      return next;
    });
    setRateState((prev) => {
      const next = { ...prev };
      for (const t of data.timeLines) {
        if (next[t.userId] === undefined) next[t.userId] = t.rate ?? "";
      }
      return next;
    });
  }, [data]);

  const { lineTotals, timeTotal, missingRateUserIds } = useMemo(
    () =>
      computeTMTimeTotals(
        (data?.timeLines ?? []).map((t) => ({ userId: t.userId, hours: t.hours })),
        rateState,
      ),
    [data, rateState],
  );

  const voucherTotal = useMemo(() => {
    if (!data) return 0;
    return data.vouchers.reduce((sum, v) => {
      const st = voucherState[v.id];
      if (!st?.include) return sum;
      return sum + v.cost * (1 + (st.markupPct || 0) / 100);
    }, 0);
  }, [data, voucherState]);

  const grandTotal = round2(timeTotal + voucherTotal);
  // VIB-994: data.blocked === true når et scopet T&M-mersalg finnes men ikke
  // er godkjent ennå (krever status='approved', samme gate som fastpris).
  const blocked = missingRateUserIds.length > 0 || !!data?.blocked;

  const handleProceed = () => {
    if (!data || blocked) return;
    const lines: TMInvoicePrefill["lines"] = [];
    for (const t of data.timeLines) {
      const rate = typeof rateState[t.userId] === "number" ? (rateState[t.userId] as number) : 0;
      lines.push({
        description: `${t.userName} — ${t.hours} timer à ${formatNOK(rate)}`,
        quantity: t.hours,
        unit_price: rate,
      });
    }
    const includedVouchers: { id: string; markupPct: number }[] = [];
    for (const v of data.vouchers) {
      const st = voucherState[v.id];
      if (!st?.include) continue;
      const markup = st.markupPct || 0;
      lines.push({
        description: `${v.supplierName}${v.description ? ` – ${v.description}` : ""}`,
        quantity: 1,
        unit_price: round2(v.cost * (1 + markup / 100)),
      });
      includedVouchers.push({ id: v.id, markupPct: markup });
    }

    onProceed({
      description: `T&M-faktura ${projectName}`,
      type: "final",
      lines,
      tmContext: {
        timeEntryIds: data.timeEntryIds,
        vouchers: includedVouchers,
        timesheet: data.timesheet,
        upsellId,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>T&M-faktura — timer og bilag</DialogTitle>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex items-center justify-center py-10">
            <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : data.blocked ? (
          <p className="py-6 text-sm text-muted-foreground">
            Mersalget må godkjennes før T&M-fakturering kan starte.
          </p>
        ) : (
          <div className="space-y-5">
            {/* Timer per ansatt med redigerbar sats */}
            <div>
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Timer</h4>
              {data.timeLines.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen ufakturerte timer.</p>
              ) : (
                <div className="space-y-2">
                  {data.timeLines.map((t) => {
                    const missing = missingRateUserIds.includes(t.userId);
                    return (
                      <div key={t.userId} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 truncate">
                          {t.userName} <span className="text-muted-foreground">— {t.hours} t</span>
                        </span>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            value={rateState[t.userId] ?? ""}
                            onChange={(e) =>
                              setRateState((s) => ({
                                ...s,
                                [t.userId]: e.target.value === "" ? "" : Number(e.target.value),
                              }))
                            }
                            placeholder="sats"
                            className={`h-8 w-24 text-right ${missing ? "border-red-300" : ""}`}
                          />
                          <span className="text-xs text-muted-foreground">kr/t</span>
                        </div>
                        <span className="w-24 text-right tabular-nums font-medium">
                          {formatNOK(lineTotals[t.userId] ?? 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {blocked && (
                <p className="mt-2 text-xs text-red-700">
                  Fyll inn timesats for alle ansatte for å gå videre.
                </p>
              )}
            </div>

            {/* Bilag med markup */}
            {data.vouchers.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Bilag (viderefaktureres)</h4>
                <div className="space-y-2">
                  {data.vouchers.map((v) => {
                    const st = voucherState[v.id] ?? { include: true, markupPct: data.defaultMarkupPct };
                    const lineTotal = st.include ? round2(v.cost * (1 + (st.markupPct || 0) / 100)) : 0;
                    return (
                      <div key={v.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={st.include}
                          onCheckedChange={(c) =>
                            setVoucherState((s) => ({ ...s, [v.id]: { ...st, include: !!c } }))
                          }
                        />
                        <span className="flex-1 truncate">
                          {v.supplierName}
                          {v.description ? ` – ${v.description}` : ""}{" "}
                          <span className="text-muted-foreground">({formatNOK(v.cost)} kost)</span>
                        </span>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={st.markupPct}
                            disabled={!st.include}
                            onChange={(e) =>
                              setVoucherState((s) => ({
                                ...s,
                                [v.id]: { ...st, markupPct: Number(e.target.value) },
                              }))
                            }
                            className="h-8 w-16 text-right"
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        <span className="w-24 text-right tabular-nums font-medium">{formatNOK(lineTotal)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="font-medium">Total (eks. mva)</span>
              <span className="text-lg font-bold tabular-nums">{formatNOK(grandTotal)}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleProceed} disabled={blocked || !data?.hasUninvoiced}>
            Gå videre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
